import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { templates } from '@sarnia/shared';
import { api, ApiError, type SiteSummary } from '../../lib/api';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Skeleton } from '../../components/ui/Skeleton';
import { Icon } from '../../components/ui/Icon';

function relativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffSeconds = Math.round((date.getTime() - now.getTime()) / 1000);
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

  const ranges: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 60 * 60 * 24 * 365],
    ['month', 60 * 60 * 24 * 30],
    ['week', 60 * 60 * 24 * 7],
    ['day', 60 * 60 * 24],
    ['hour', 60 * 60],
    ['minute', 60],
    ['second', 1],
  ];

  for (const [unit, seconds] of ranges) {
    if (Math.abs(diffSeconds) >= seconds || unit === 'second') {
      return formatter.format(Math.round(diffSeconds / seconds), unit);
    }
  }
  return formatter.format(0, 'second');
}

export function MySitesPage() {
  const [sites, setSites] = useState<SiteSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'published'>('all');

  useEffect(() => {
    let cancelled = false;
    api
      .getSites()
      .then((data) => {
        if (!cancelled) setSites(data.sites);
      })
      .catch((err) => {
        if (!cancelled) {
          if (err instanceof ApiError) setError(err.message);
          else setError('Could not load your sites.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredSites = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return sites.filter((site) => {
      const matchesSearch = !query || site.title.toLowerCase().includes(query);
      const matchesStatus = statusFilter === 'all' || site.state === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [sites, searchTerm, statusFilter]);

  if (loading) {
    return (
      <div className="dash-page">
        <div className="dash-page-header">
          <h1>My sites</h1>
        </div>
        <div className="dash-site-grid">
          <Skeleton height="16rem" />
          <Skeleton height="16rem" />
          <Skeleton height="16rem" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dash-page">
        <div className="dash-page-header">
          <h1>My sites</h1>
        </div>
        <Alert variant="error">{error}</Alert>
        <Button variant="primary" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="dash-page">
      <div className="dash-page-header">
        <div>
          <h1>My sites</h1>
          <p className="dash-page-subtitle">
            {sites.length} {sites.length === 1 ? 'site' : 'sites'}
          </p>
        </div>
        <Link className="sarnia-btn sarnia-btn--primary" to="/dashboard/sites/new">
          <Icon name="plus" size={16} />
          <span>New site</span>
        </Link>
      </div>

      <div className="dash-toolbar">
        <label className="dash-search">
          <Icon name="search" size={16} />
          <input
            type="search"
            placeholder="Search sites"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </label>
        <select
          className="dash-status-filter"
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value as 'all' | 'draft' | 'published')}
          aria-label="Filter by status"
        >
          <option value="all">All</option>
          <option value="published">Published</option>
          <option value="draft">Drafts</option>
        </select>
      </div>

      {sites.length === 0 ? (
        <div className="dash-empty">
          <div className="dash-empty__icon" aria-hidden="true">
            <Icon name="layout" size={28} />
          </div>
          <h2>No sites yet</h2>
          <p>Your first Sarnia site starts with a template or a blank canvas.</p>
          <Link className="sarnia-btn sarnia-btn--primary" to="/dashboard/sites/new">
            Create your first site
          </Link>
        </div>
      ) : (
        <div className="dash-site-grid">
          {filteredSites.map((site) => {
            const template = templates.find((entry) => entry.id === site.templateId);
            const templateName = template?.name ?? 'Custom template';
            const isPublished = site.state === 'published' && Boolean(site.slug);
            const initial = site.title.charAt(0).toUpperCase() || 'S';
            const updatedLabel = relativeTime(site.updatedAt);

            return (
              <article key={site.id} className="sarnia-card dash-site-card">
                <div className="dash-site-card__preview">
                  {template?.previewImage ? (
                    <img
                      src={template.previewImage}
                      alt=""
                      className="dash-site-card__image"
                      loading="lazy"
                    />
                  ) : (
                    <div className="dash-site-card__placeholder" aria-hidden="true">
                      <span>{initial}</span>
                    </div>
                  )}
                  <span
                    className={`sarnia-badge ${
                      site.state === 'published'
                        ? 'sarnia-badge--success'
                        : site.state === 'suspended'
                          ? 'sarnia-badge--danger'
                          : 'sarnia-badge--neutral'
                    }`}
                  >
                    {site.state === 'published' ? 'Shared' : site.state === 'draft' ? 'Draft' : 'Suspended'}
                  </span>
                </div>
                <div className="dash-site-card__body">
                  <h2>{site.title}</h2>
                  <p className="dash-site-card__meta">
                    {templateName} · Updated {updatedLabel} · v{site.version}
                  </p>
                  <div className="dash-site-card__actions">
                    <Link className="sarnia-btn sarnia-btn--sm sarnia-btn--primary" to={`/editor/${site.id}`}>
                      Edit
                    </Link>
                    <Link
                      className="sarnia-btn sarnia-btn--sm sarnia-btn--secondary"
                      to={`/dashboard/sites/${site.id}/settings`}
                    >
                      Settings
                    </Link>
                    {isPublished && (
                      <a
                        className="dash-site-open"
                        href={`https://${site.slug}.sarnia.digital`}
                        target="_blank"
                        rel="noreferrer"
                        aria-label="Open preview"
                        title="Open preview"
                      >
                        <Icon name="globe" size={18} />
                      </a>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {sites.length > 0 && filteredSites.length === 0 && (
        <div className="dash-empty dash-empty--compact">
          <p>No sites match your search.</p>
        </div>
      )}
    </div>
  );
}
